import React, { useEffect } from 'react';
import { css } from '@emotion/react';

import AdSlot from '@gumtree/third-party-ads/src/components/ad-slot';
import { getL1CategoryIdBySeoName } from '@gumtree/shared/src/util/category-mapping';
import { PageLayout } from '@gumtree/shared/src/types/srp';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { CINCH_CAR_ACCOUNT_ID } from '@gumtree/shared/src/constants/cinch-car';
import type { SearchAd } from '@gumtree/shared/src/model/search-ads-model';
import type { SellerAd } from '@gumtree/shared/src/model/seller-other-ads-model';
import WithVisibilityObserver from '@gumtree/shared/src/util/element-visibility/visibility-observer';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { getClickListingDetails } from '@gumtree/shared/src/util/listing-details';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';

import { DisplayAdSlotDivNames } from '@gumtree/third-party-ads/src/framework/dfp/ad-config/slot-configs';
import TextAds from '@gumtree/third-party-ads/src/framework/adsense-admarketplace/TextAds';
import { isAdSenseAdmarketEnabled } from '@gumtree/third-party-ads/src/framework/adsense-admarketplace/adsense/initialisation-util';
import { breakpoints, gutterSizes, mediaQuery } from '../base/theme';
import { ListingTile, ListingTileNew } from '..';
import { AdsProps } from './interface';
import DeliveryTile from '../listing-tile-v-2023/delivery-tile';

const AMOUNT_WITHOUT_LAZY_LOADING = 2;

const cardStyle = (pageLayout: PageLayout, id: string) => css`
    grid-column: span 12;

    ${id === 'premiumNative1' &&
    `
        display: none;
        box-shadow: 0 0 12px 0 rgb(0 0 0 / 20%);
        border-radius: 8px;
    `}

    ${id === 'integratedMpu' &&
    `
        margin: 16px 0;
    `}

    ${pageLayout === PageLayout.TwoColumns &&
    `
    grid-column: span 6;

    > article {
      box-shadow: none;
    }
  `}

    ${pageLayout === PageLayout.ThreeColumns &&
    `
    grid-column: span 4;

    > article {
      height: 100%; // ensure tiles match height if other tiles are taller
    }
  `}
`;

const ResultsList = ({
    ads,
    gamSlotsDisplayConfig,
    pageLayout,
    newUi,
    updateRequestQuoteFormData,
    requestId,
    showDeliveryTile,
    showPostDate = true,
    setOpenHPModal,
    adSenseConfig,
}: AdsProps) => {
    const isNewUiExperiment = useFeatureValue(GrowthBookFeature.SERVICE_NEW_UI, 'A') === 'B';
    const adsenceAdmarketplaceValue = useFeatureValue(GrowthBookFeature.ADSENCE_ADMARKETPLACE, 'A');

    const isAdSenseEnable = adSenseConfig
        ? isAdSenseAdmarketEnabled({
              pageType: adSenseConfig.pageType as any,
              l1Category: adSenseConfig.l1Category as any,
              currentPage: adSenseConfig.currentPage,
              resultSize: adSenseConfig.resultSize as any,
          } as any)
        : false;

    useEffect(() => {
        const standoutAds = ads.filter(({ standout }) => standout);
        trackV2('ResultEyeCatcherAdShown', `${standoutAds.length}`, {
            uaNonInt: true,
        });
    }, [ads]);

    function onListItemVisible(ad, index) {
        let listingReviewSource = ad.sellerRating?.type || 'none';
        if (!ad.sellerRating?.total || ad.sellerRating?.total === 0) {
            listingReviewSource = 'none';
        }
        trackGA4Event<GA4.ListingVisibleEvent>({
            event: 'impress_listing',
            linkDomain: window.location.host,
            linkURL: ad.path,
            linkType: 'card',
            linkText: undefined,
            clickListingDetails: getClickListingDetails(ad),
            listingPosition: index,
            listingSource: ad.adSource?.toLowerCase() || 'gt',
            accountId: ad.accountId?.toString() || '',
            listingReviewSource,
            requestId,
        });
    }

    return (
        <div
            css={css`
                width: 100%;
                display: grid;
                grid-template-columns: repeat(
                    12,
                    minmax(0, 1fr)
                ); // https://stackoverflow.com/a/61240964
                grid-gap: ${gutterSizes.large};
                grid-auto-flow: dense;
                margin-bottom: 56px;

                ${mediaQuery.until(breakpoints.small)} {
                    ${pageLayout === PageLayout.TwoColumns && `grid-gap: ${gutterSizes.base};`}
                }

                ${mediaQuery.from(breakpoints.large)} {
                    ${pageLayout === PageLayout.ThreeColumns &&
                    `
                      column-gap: ${gutterSizes.xlarge};
                      row-gap: ${gutterSizes.xxlarge};
                    `}
                }
            `}
        >
            {ads.map((ad, index) => {
                const typedAd = ad as SearchAd | SellerAd;

                const advertisingSlotConfig = gamSlotsDisplayConfig?.find((slotConfig) => {
                    return (
                        (pageLayout === PageLayout.OneColumn
                            ? slotConfig.listViewPosition
                            : slotConfig.gridViewPosition) === index
                    );
                });

                const isSellerCinchCar = typedAd.accountId === CINCH_CAR_ACCOUNT_ID;
                const isService = ad.l1CategoryId === getL1CategoryIdBySeoName('business-services');
                const useNewUi = isNewUiExperiment && isService && newUi;
                const showAdSenseSlot = ((pageLayout === PageLayout.OneColumn && index === 5) || (pageLayout !== PageLayout.OneColumn && index === 10)) && adSenseConfig && isAdSenseEnable;

                return (
                    <React.Fragment key={`${typedAd.id}${typedAd.featured ? '-featured' : ''}`}>
                        {advertisingSlotConfig && (
                            <AdSlot
                                css={cardStyle(
                                    advertisingSlotConfig.isAlwaysFullWidth
                                        ? PageLayout.OneColumn
                                        : pageLayout,
                                    advertisingSlotConfig.id
                                )}
                                id={DisplayAdSlotDivNames[advertisingSlotConfig.id]}
                            />
                        )}
                        {showAdSenseSlot && (
                            <TextAds
                                pageType={adSenseConfig.pageType as any}
                                l1Category={adSenseConfig.l1Category as any}
                                device={adSenseConfig.device as any}
                                currentPage={adSenseConfig.currentPage}
                                isZeroResults={adSenseConfig.isZeroResults}
                                resultSize={adSenseConfig.resultSize}
                                query={adSenseConfig.query}
                                isQA={adSenseConfig.isQA}
                                slot={adSenseConfig.slot || 'afscontainer1'}
                                adsenceAdmarketplaceValue={adsenceAdmarketplaceValue}
                                style={{
                                    'gridColumn': 'span 12',
                                }}
                            />
                        )}
                        <WithVisibilityObserver
                            parent="body"
                            onVisible={() => onListItemVisible(ad, index)}
                            css={cardStyle(pageLayout, typedAd.id.toString())}
                        >
                            {!useNewUi && (
                                <ListingTile
                                    pageLayout={pageLayout}
                                    isLazyLoadImage={index >= AMOUNT_WITHOUT_LAZY_LOADING}
                                    ad={{ ...ad, ...(isSellerCinchCar ? { location: '' } : {}) }}
                                    showPostDate={showPostDate}
                                    setOpenHPModal={setOpenHPModal}
                                />
                            )}
                            {useNewUi && (
                                <ListingTileNew
                                    pageLayout={pageLayout}
                                    isLazyLoadImage={index >= AMOUNT_WITHOUT_LAZY_LOADING}
                                    ad={{ ...ad, ...(isSellerCinchCar ? { location: '' } : {}) }}
                                    updateRequestQuoteFormData={updateRequestQuoteFormData}
                                    requestId={requestId}
                                />
                            )}
                        </WithVisibilityObserver>
                        {((ads.length >= 3 && index === 2) ||
                            (ads.length < 3 && index === ads.length - 1)) &&
                            showDeliveryTile && (
                                <div css={cardStyle(pageLayout, typedAd.id.toString())}>
                                    <DeliveryTile pageLayout={pageLayout} />
                                </div>
                            )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default ResultsList;
